import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import {
  ShoppingCart, Plus, Minus, Trash2, X, ChevronUp, Send, Loader2,
  UtensilsCrossed, Package, CheckCircle2, MessageSquare, Bell
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MenuCart = ({ tableId, tableLabel, restaurantName, orderingEnabled, primaryColor, paymentMode, stripeConfigured, orderType, onOrderTypeChange }) => {
  const [cart, setCart] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [customerName, setCustomerName] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [tableNumber, setTableNumber] = useState('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [orderResult, setOrderResult] = useState(null);
  const [payOnline, setPayOnline] = useState(false);

  const canPayOnline = stripeConfigured && (paymentMode === 'stripe' || paymentMode === 'both');

  // Auto-set payOnline when stripe-only mode
  React.useEffect(() => {
    if (canPayOnline && paymentMode === 'stripe') setPayOnline(true);
  }, [canPayOnline, paymentMode]);

  // Expose addToCart globally for PublicMenu to call
  React.useEffect(() => {
    if (!orderingEnabled) return;
    window.__menuCart = { addToCart };
    return () => { delete window.__menuCart; };
  });

  if (!orderingEnabled) return null;

  const addToCart = (dish) => {
    setCart(prev => {
      const existing = prev.find(item => item.dish_id === dish.id);
      if (existing) {
        return prev.map(item =>
          item.dish_id === dish.id ? { ...item, quantity: item.quantity + 1 } : item
        );
      }
      return [...prev, {
        dish_id: dish.id,
        dish_name: dish.name,
        quantity: 1,
        price: dish.price,
        price_takeaway: dish.price_takeaway ?? null,
        notes: ''
      }];
    });
    toast.success(`${dish.name} ajouté au panier`, { duration: 1500 });
  };

  // Price depends on the selected order type (dual pricing)
  const getUnitPrice = (item) =>
    orderType === 'takeaway' && item.price_takeaway != null ? item.price_takeaway : item.price;

  const updateQuantity = (dishId, delta) => {
    setCart(prev => prev.map(item => {
      if (item.dish_id !== dishId) return item;
      const newQty = item.quantity + delta;
      return newQty <= 0 ? null : { ...item, quantity: newQty };
    }).filter(Boolean));
  };

  const removeItem = (dishId) => {
    setCart(prev => prev.filter(item => item.dish_id !== dishId));
  };

  const total = cart.reduce((sum, item) => sum + getUnitPrice(item) * item.quantity, 0);
  const itemCount = cart.reduce((sum, item) => sum + item.quantity, 0);

  const submitOrder = async () => {
    if (cart.length === 0) return;
    setSubmitting(true);
    try {
      // Step 1: Create order
      const res = await fetch(`${API_URL}/api/public/orders`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: cart.map(item => ({
            dish_id: item.dish_id,
            dish_name: item.dish_name,
            quantity: item.quantity,
            unit_price: getUnitPrice(item),
            notes: item.notes
          })),
          order_type: orderType,
          table_id: tableId || null,
          table_label: tableLabel || (orderType === 'dine_in' && tableNumber.trim() ? `Table ${tableNumber.trim()}` : null),
          customer_name: customerName,
          customer_phone: customerPhone,
          notes
        })
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Erreur');
      }
      const data = await res.json();
      
      // Step 2: If paying online with Stripe, redirect to Stripe Checkout
      if (payOnline && canPayOnline) {
        const payRes = await fetch(`${API_URL}/api/public/orders/${data.id}/pay`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ origin_url: window.location.origin })
        });
        if (!payRes.ok) {
          const err = await payRes.json();
          throw new Error(err.detail || 'Erreur paiement');
        }
        const payData = await payRes.json();
        // Redirect to Stripe Checkout
        window.location.href = payData.url;
        return;
      }
      
      // On-site payment: show success
      setOrderResult(data);
      setCart([]);
    } catch (e) {
      toast.error(e.message || "Erreur lors de l'envoi de la commande");
    } finally { setSubmitting(false); }
  };

  // Success view
  if (orderResult) {
    return (
      <div className="fixed inset-0 z-[9999] bg-black/50 flex items-end sm:items-center justify-center" onClick={() => setOrderResult(null)}>
        <div className="bg-white w-full sm:max-w-md sm:rounded-2xl rounded-t-2xl p-6 space-y-4 animate-in slide-in-from-bottom" onClick={e => e.stopPropagation()} data-testid="order-success">
          <div className="text-center space-y-3">
            <CheckCircle2 className="h-14 w-14 mx-auto" style={{ color: primaryColor }} />
            <h2 className="font-heading text-xl font-bold text-gray-900">Commande envoyée !</h2>
            <p className="text-gray-500">Votre commande <strong>{orderResult.order_number}</strong> a été transmise au restaurant.</p>
            {orderResult.table_label && <Badge variant="outline" className="text-sm">{orderResult.table_label}</Badge>}
          </div>
          <div className="bg-gray-50 rounded-xl p-3 space-y-1">
            {orderResult.items?.map((item, i) => (
              <div key={i} className="flex justify-between text-sm">
                <span>{item.quantity}x {item.dish_name}</span>
                <span className="font-medium">{(item.unit_price * item.quantity).toFixed(2)} €</span>
              </div>
            ))}
            <div className="border-t pt-1 mt-1 flex justify-between font-bold">
              <span>Total</span><span>{orderResult.total?.toFixed(2)} €</span>
            </div>
          </div>
          <p className="text-xs text-center text-gray-400">
            Paiement en caisse
          </p>
          <div className="space-y-2">
            {orderResult.tracking_token && (
              <Button
                onClick={() => { window.location.href = `/order/${orderResult.tracking_token}`; }}
                className="w-full rounded-full text-white"
                style={{ backgroundColor: primaryColor }}
                data-testid="track-order-btn"
              >
                <Bell className="h-4 w-4 mr-2" /> Suivre ma commande
              </Button>
            )}
            <Button onClick={() => setOrderResult(null)} variant="outline" className="w-full rounded-full">
              Fermer
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Floating cart button */}
      {itemCount > 0 && !isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          className="fixed bottom-6 right-6 z-[9998] flex items-center gap-2 px-5 py-3 rounded-full text-white font-semibold shadow-2xl transition-transform hover:scale-105"
          style={{ backgroundColor: primaryColor }}
          data-testid="cart-fab"
        >
          <ShoppingCart className="h-5 w-5" />
          <span>{itemCount} article{itemCount > 1 ? 's' : ''}</span>
          <span className="bg-white/20 px-2 py-0.5 rounded-full text-sm">{total.toFixed(2)} €</span>
        </button>
      )}

      {/* Cart panel */}
      {isOpen && (
        <div className="fixed inset-0 z-[9999] bg-black/50" onClick={() => setIsOpen(false)}>
          <div className="absolute bottom-0 left-0 right-0 bg-white rounded-t-2xl max-h-[85vh] overflow-y-auto animate-in slide-in-from-bottom"
            onClick={e => e.stopPropagation()} data-testid="cart-panel">
            {/* Header */}
            <div className="sticky top-0 bg-white border-b px-4 py-3 flex items-center justify-between z-10">
              <div className="flex items-center gap-2">
                <ShoppingCart className="h-5 w-5" style={{ color: primaryColor }} />
                <span className="font-heading font-bold">Mon panier ({itemCount})</span>
              </div>
              <button onClick={() => setIsOpen(false)} className="p-1 hover:bg-gray-100 rounded-full"><X className="h-5 w-5 text-gray-400" /></button>
            </div>

            <div className="p-4 space-y-4">
              {/* Order type */}
              {!tableId && (
                <div className="space-y-2">
                  <div className="flex gap-2">
                    <button onClick={() => onOrderTypeChange('dine_in')}
                      className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-medium border-2 transition-all ${orderType === 'dine_in' ? 'border-orange-400 bg-orange-50 text-orange-700' : 'border-gray-100 text-gray-500'}`}
                      data-testid="order-type-dine-in">
                      <UtensilsCrossed className="h-4 w-4" /> Sur place
                    </button>
                    <button onClick={() => onOrderTypeChange('takeaway')}
                      className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-medium border-2 transition-all ${orderType === 'takeaway' ? 'border-orange-400 bg-orange-50 text-orange-700' : 'border-gray-100 text-gray-500'}`}
                      data-testid="order-type-takeaway">
                      <Package className="h-4 w-4" /> A emporter
                    </button>
                  </div>
                  {orderType === 'dine_in' && (
                    <div>
                      <Label className="text-xs text-gray-500">Numéro de table (si vous êtes installé)</Label>
                      <Input value={tableNumber} onChange={e => setTableNumber(e.target.value)} placeholder="Ex : 12" className="h-9 text-sm" data-testid="order-table-number" />
                    </div>
                  )}
                </div>
              )}

              {tableId && tableLabel && (
                <div className="bg-gray-50 rounded-xl p-3 text-center">
                  <p className="text-sm text-gray-500">Commande pour</p>
                  <p className="font-heading font-bold text-gray-900">{tableLabel}</p>
                </div>
              )}

              {/* Items */}
              {cart.length === 0 ? (
                <div className="text-center py-8 text-gray-400">
                  <ShoppingCart className="h-10 w-10 mx-auto mb-2" />
                  <p className="text-sm">Votre panier est vide</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {cart.map(item => (
                    <div key={item.dish_id} className="flex items-center gap-3 bg-gray-50 rounded-xl p-3">
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm text-gray-900 truncate">{item.dish_name}</p>
                        <p className="text-xs text-gray-500">{getUnitPrice(item).toFixed(2)} € / unité</p>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <button onClick={() => updateQuantity(item.dish_id, -1)} className="h-7 w-7 rounded-full bg-gray-200 flex items-center justify-center hover:bg-gray-300">
                          <Minus className="h-3.5 w-3.5" />
                        </button>
                        <span className="w-6 text-center font-bold text-sm">{item.quantity}</span>
                        <button onClick={() => updateQuantity(item.dish_id, 1)} className="h-7 w-7 rounded-full flex items-center justify-center text-white" style={{ backgroundColor: primaryColor }}>
                          <Plus className="h-3.5 w-3.5" />
                        </button>
                      </div>
                      <span className="font-bold text-sm w-16 text-right">{(getUnitPrice(item) * item.quantity).toFixed(2)} €</span>
                      <button onClick={() => removeItem(item.dish_id)} className="text-gray-300 hover:text-red-500">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* Customer info */}
              {cart.length > 0 && (
                <>
                  <div className="space-y-3 border-t pt-3">
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <Label className="text-xs text-gray-500">Nom (optionnel)</Label>
                        <Input value={customerName} onChange={e => setCustomerName(e.target.value)} placeholder="Votre nom" className="h-9 text-sm" data-testid="order-name" />
                      </div>
                      <div>
                        <Label className="text-xs text-gray-500">Téléphone (optionnel)</Label>
                        <Input value={customerPhone} onChange={e => setCustomerPhone(e.target.value)} placeholder="06..." className="h-9 text-sm" data-testid="order-phone" />
                      </div>
                    </div>
                    <div>
                      <Label className="text-xs text-gray-500">Note pour le restaurant</Label>
                      <Input value={notes} onChange={e => setNotes(e.target.value)} placeholder="Sans oignon, bien cuit..." className="h-9 text-sm" data-testid="order-notes" />
                    </div>
                  </div>

                  {/* Total + Submit */}
                  <div className="border-t pt-3 space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="font-heading text-lg font-bold text-gray-900">Total</span>
                      <span className="font-heading text-xl font-bold" style={{ color: primaryColor }}>{total.toFixed(2)} €</span>
                    </div>

                    {/* Payment choice */}
                    {canPayOnline && paymentMode === 'both' && (
                      <div className="flex gap-2">
                        <button onClick={() => setPayOnline(false)}
                          className={`flex-1 py-2.5 rounded-xl text-xs font-medium border-2 transition-all ${!payOnline ? 'border-orange-400 bg-orange-50 text-orange-700' : 'border-gray-100 text-gray-500'}`}
                          data-testid="pay-on-site-btn">
                          Payer en caisse
                        </button>
                        <button onClick={() => setPayOnline(true)}
                          className={`flex-1 py-2.5 rounded-xl text-xs font-medium border-2 transition-all ${payOnline ? 'border-purple-400 bg-purple-50 text-purple-700' : 'border-gray-100 text-gray-500'}`}
                          data-testid="pay-online-btn">
                          Payer en ligne
                        </button>
                      </div>
                    )}

                    <Button onClick={submitOrder} disabled={submitting || cart.length === 0}
                      className="w-full h-12 rounded-full text-white font-semibold text-base"
                      style={{ backgroundColor: (payOnline && canPayOnline) ? '#7c3aed' : primaryColor }}
                      data-testid="submit-order-btn">
                      {submitting ? (
                        <><Loader2 className="h-5 w-5 animate-spin mr-2" />Envoi en cours...</>
                      ) : (payOnline && canPayOnline) ? (
                        <><Send className="h-5 w-5 mr-2" />Payer en ligne ({total.toFixed(2)} €)</>
                      ) : (
                        <><Send className="h-5 w-5 mr-2" />Commander ({total.toFixed(2)} €)</>
                      )}
                    </Button>
                    <p className="text-xs text-center text-gray-400">
                      {(payOnline && canPayOnline) ? 'Paiement sécurisé par Stripe' : 'Paiement sur place en caisse'}
                    </p>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default MenuCart;
